import { observer } from "mobx-react";
import React from "react";
import TermIcon from "../../../../../images/membership/term.svg";
import { PricingPageService } from "../services";
import StandardPricing from "./StandardPricing";
import TierPricingComponent from "./TierPricingComponent";
import PackagePricing from "./PackagePricing";
import CustomPricing from "./CustomPricing";
import PricingOptionRow from "./PricingOptionRow";
import { PricingModel, RecurringBillingInterval } from "../enums";
import {
  Collapsible,
  CustomLabel,
  FlexContainer,
  InfoIcon,
  Input,
  SingleSelect,
} from "@mindmemobile/mm-shared-global";
import {
  DescriptionCell,
  LabelCell,
  ProductDetailWrapper,
  RecurringWrapper,
  SetupDetails,
} from "../styles";
import {
  CustomPricingInputModel,
  PackagePricingInputModel,
  PricePageTermsInputModel,
  PriceTierInputData,
  PricingPageInputModel,
  StandardPricingInputModel,
} from "../models";
import { getBillingOptions } from "./RecurringSection";

type Props = {
  pricingPageData?: PricingPageService;
  index: number;
  showPricingOptions?: boolean;
  isServicePricingPage?: boolean;
};
@observer
class TermsSection extends React.Component<Props> {
  get editablePrice() {
    return this.props.pricingPageData?.prices[this.props.index];
  }

  get isOpen(): boolean {
    return this.editablePrice?.isTermsOpen === true;
  }

  get options() {
    return getBillingOptions(
      "billingInterval",
      parseFloat(this.editablePrice?.termSettings?.termIntervalCount || "0") > 1
    );
  }

  get lengthSection() {
    return (
      <FlexContainer className="label-description-row">
        <LabelCell>
          <CustomLabel lineHeight="40px" fontSize="16px">
            Length
          </CustomLabel>
          <InfoIcon />
        </LabelCell>
        <DescriptionCell>
          <FlexContainer spacing={15} fxWrap="wrap" className="price-field">
            {this.editablePrice?.termSettings?.intervalApplies && (
              <>
                <Input
                  value={
                    this.editablePrice?.termSettings?.termIntervalCount || ""
                  }
                  type="number"
                  error={this.hasError("termIntervalCount")}
                  onChange={(e) =>
                    this.updateValue(e.target.value, "termIntervalCount")
                  }
                />
                <SingleSelect
                  searchable={false}
                  hasError={this.hasError("termInterval")}
                  selectedKey={
                    this.editablePrice?.termSettings?.termInterval || ""
                  }
                  nameKeys={["label"]}
                  valueKeys={["value"]}
                  options={[this.options]}
                  onChange={(option) =>
                    this.updateValue(
                      option.value as RecurringBillingInterval,
                      "termInterval"
                    )
                  }
                  className="dpdn-no-separator dropup"
                />
              </>
            )}
            {!this.props.isServicePricingPage && (
              <div>
                {this.checkBox(
                  this.editablePrice?.termSettings?.intervalApplies === false,
                  "intervalApplies",
                  "Lifetime"
                )}
              </div>
            )}
          </FlexContainer>
        </DescriptionCell>
      </FlexContainer>
    );
  }

  toggleTerms = () => {
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        isTermsOpen: !this.isOpen,
      }),
      this.props.index
    );
  };

  updatePricing = (standardPricing: StandardPricingInputModel) => {
    if (!this.editablePrice) return;
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        termSettings: new PricePageTermsInputModel({
          ...this.editablePrice?.termSettings,
          standardPricing: standardPricing,
        }),
      }),
      this.props.index
    );
  };

  updateValue(
    value: string | boolean,
    fieldName: keyof PricePageTermsInputModel
  ) {
    if (!this.editablePrice?.termSettings) return;
    let termSettings = new PricePageTermsInputModel({
      ...this.editablePrice.termSettings,
    });
    switch (fieldName) {
      case "termIntervalCount":
        termSettings = new PricePageTermsInputModel({
          ...this.editablePrice.termSettings,
          termIntervalCount: value.toString(),
        });
        break;
      case "termInterval":
        termSettings = new PricePageTermsInputModel({
          ...this.editablePrice.termSettings,
          termInterval: value as RecurringBillingInterval,
        });
        break;
      case "intervalApplies":
        termSettings = new PricePageTermsInputModel({
          ...this.editablePrice.termSettings,
          intervalApplies: !Boolean(value),
        });
        break;
      case "pricingModel":
        termSettings = new PricePageTermsInputModel({
          ...this.editablePrice.termSettings,
          pricingModel: value as PricingModel,
        });
    }
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        termSettings: termSettings,
      }),
      this.props.index
    );
  }

  checkBox(
    value: boolean,
    fieldName: keyof PricePageTermsInputModel,
    label: string
  ) {
    return (
      <Input
        value={value}
        id={`chk${fieldName}`}
        type="checkbox"
        label={label}
        onChange={(e) => this.updateValue(e.target.checked, fieldName)}
        className=""
      />
    );
  }

  hasError(fieldName: keyof PricePageTermsInputModel) {
    if (!this.editablePrice?.termSettings) return false;

    return (
      this.editablePrice?.termSettings?.errors?.find(
        (error) => error.field === fieldName
      ) !== undefined
    );
  }

  get volumePrice(): number {
    return (
      parseFloat(
        this.editablePrice?.termSettings?.volumePricing?.price || ""
      ) || 0
    );
  }
  get tieredPrice(): number {
    return (
      parseFloat(
        this.editablePrice?.termSettings?.tieredPricing?.price || ""
      ) || 0
    );
  }
  get getPricingInputs(): React.ReactNode {
    switch (this.editablePrice?.termSettings?.pricingModel) {
      case PricingModel.Standard:
        return (
          <StandardPricing
            standardPricing={
              this.editablePrice?.termSettings?.standardPricing ||
              new StandardPricingInputModel({ showSetUpFee: true })
            }
            onUpdate={(updatedStandardPricing) =>
              this.updatePricingOptions(
                updatedStandardPricing,
                "standardPricing"
              )
            }
          />
        );
      case PricingModel.Volume:
        return (
          <TierPricingComponent
            totalPrice={this.volumePrice}
            pricingType={PricingModel.Volume}
            tier={
              this.editablePrice?.termSettings?.volumePricing ||
              new PriceTierInputData({ showSetUpFee: true })
            }
            onUpdateTier={(updatedValue) =>
              this.updatePricingOptions(updatedValue, "volumePricing")
            }
          />
        );
      case PricingModel.Tiered:
        return (
          <TierPricingComponent
            totalPrice={this.tieredPrice}
            pricingType={PricingModel.Tiered}
            tier={
              this.editablePrice?.termSettings?.tieredPricing ||
              new PriceTierInputData({ showSetUpFee: true })
            }
            onUpdateTier={(updatedValue) =>
              this.updatePricingOptions(updatedValue, "tieredPricing")
            }
          />
        );
      case PricingModel.Package:
        return (
          <PackagePricing
            package={
              this.editablePrice?.termSettings?.packagePricing ||
              new PackagePricingInputModel({ showSetUpFee: true })
            }
            onUpdate={(updatedValue) =>
              this.updatePricingOptions(updatedValue, "packagePricing")
            }
          />
        );
      case PricingModel.Custom:
        return (
          <CustomPricing
            onUpdate={(updatedValue) =>
              this.updatePricingOptions(updatedValue, "customPricing")
            }
            customPricing={
              this.editablePrice?.termSettings?.customPricing ||
              new CustomPricingInputModel({ showSetUpFee: true })
            }
          />
        );
      default:
        return <></>;
    }
  }

  updatePricingOptions(
    value:
      | StandardPricingInputModel
      | PackagePricingInputModel
      | PriceTierInputData
      | CustomPricingInputModel,
    fieldName: keyof PricePageTermsInputModel
  ) {
    if (!this.editablePrice?.termSettings) return;
    let termSettings = new PricePageTermsInputModel({
      ...this.editablePrice.termSettings,
    });
    switch (fieldName) {
      case "standardPricing":
        termSettings = new PricePageTermsInputModel({
          ...this.editablePrice.termSettings,
          standardPricing: value as StandardPricingInputModel,
        });
        break;
      case "packagePricing":
        termSettings = new PricePageTermsInputModel({
          ...this.editablePrice.termSettings,
          packagePricing: value as PackagePricingInputModel,
        });
        break;
      case "volumePricing":
        termSettings = new PricePageTermsInputModel({
          ...this.editablePrice.termSettings,
          volumePricing: value as PriceTierInputData,
        });
        break;
      case "tieredPricing":
        termSettings = new PricePageTermsInputModel({
          ...this.editablePrice.termSettings,
          tieredPricing: value as PriceTierInputData,
        });
        break;
      case "customPricing":
        termSettings = new PricePageTermsInputModel({
          ...this.editablePrice.termSettings,
          customPricing: value as CustomPricingInputModel,
        });
        break;
    }
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        termSettings: termSettings,
      }),
      this.props.index
    );
  }
  render(): React.ReactNode {
    return (
      <RecurringWrapper>
        <ProductDetailWrapper>
          <SetupDetails>
            <CustomLabel
              fontSize="22px"
              fontWeight="bold"
              lineHeight="22px"
              margin="40px 0px 0px"
              className="setup"
            >
              <img src={TermIcon} /> Term
              <i
                className={`fa fa-dark-grey clickable ${this.isOpen ? "fa-chevron-down" : "fa-chevron-right"}`}
                onClick={() => this.toggleTerms()}
              ></i>
            </CustomLabel>
            <Collapsible isOpen={this.editablePrice?.isTermsOpen === true}>
              {this.props.showPricingOptions && (
                <PricingOptionRow
                  hasError={this.hasError("pricingModel")}
                  selectedValue={
                    this.editablePrice?.termSettings?.pricingModel || ""
                  }
                  onChange={(option) =>
                    this.updateValue(option, "pricingModel")
                  }
                />
              )}
              {this.getPricingInputs}
              {this.lengthSection}
            </Collapsible>
          </SetupDetails>
        </ProductDetailWrapper>
      </RecurringWrapper>
    );
  }
}

export default TermsSection;
